// BOSS FORMS — transformation sequence + form effects (owner 2026-11-02). Render-only: reads Sim state and the
// bossTransform / bossSlam / bossMark / bossMarkHit events (src/core/bossForms.ts).
//   beat start  → energy converges on the boss (swirling motes, rising light columns, a slow ground ring), it rises and braces
//   burst       → white-hot pop + coloured blast, a wide shockwave ring at the sim's shove radius, shell debris, dust ring,
//                 ground flash, a brief camera push-in (scaled by Camera shake) and shake; lights / flashes go through the
//                 FLASH limiter (fx.ts primitives), the full-screen impact dim is main.ts (FLASH.big())
//   new form    → accent sparks + orbiting motes (bossFormLooks.ts `orbit`)
//   slams / marks → shock rings, dust, debris (the mark telegraph decals are hazardView.ts)
import type { Sim, SimEvent } from '../core/sim';
import { TRANSFORM_BURST, TRANSFORM_T } from '../core/bossForms';
import type { Fx } from './fx';
import type { GroundGlow } from './groundGlow';
import { formLook } from './bossFormLooks';
import { FLASH } from './a11y';

type Ev = Extract<SimEvent, { t: 'bossTransform' | 'bossSlam' | 'bossMark' | 'bossMarkHit' }>;
const R = Math.random;

export class BossFormFx {
  /** camera push-in 0..1 (view.updateCamera scales it by the Camera shake setting) and where it leans */
  push = 0; pushX = 0; pushZ = 0;
  /** hitstop the view should apply (consumed by the view) */
  hitstop = 0;
  private orbitA = 0;
  constructor(private fx: Fx, private glow: GroundGlow, private ground: (x: number, z: number) => number, private lowQ: boolean) {}

  clear(): void { this.push = 0; this.hitstop = 0; }

  event(ev: Ev): void {
    const fx = this.fx;
    switch (ev.t) {
      case 'bossTransform': {
        const gy = this.ground(ev.x, ev.z), c = ev.accent;
        if (ev.phase === 'start') {
          fx.rings.add(ev.x, gy + 0.15, ev.z, 7, TRANSFORM_BURST, c, 1.6, 2);
          fx.rings.add(ev.x, gy + 0.2, ev.z, 4, TRANSFORM_BURST * 0.8, 0xffffff, 1.2, 1);
          this.glow.flash(ev.x, ev.z, 8, c, 0.6, TRANSFORM_BURST);
          fx.shake = Math.max(fx.shake, 0.25);
        } else if (ev.phase === 'burst') {
          const y = gy + 4.5;
          // a short fireball at the core (small, so its smoke clears fast: the new form must be seen at once)
          fx.blast?.blast(ev.x, y, ev.z, 1.6, false, c);
          fx.stars.add(ev.x, y, ev.z, 7, 0.24, 0xfff7ea, 1.25);
          fx.stars.add(ev.x, y + 1, ev.z, 12, 0.4, c, 2.2);
          fx.particles.emit(ev.x, y, ev.z, this.lowQ ? 30 : 60, 22, c, 0.7, 0.7, 2, 4);
          fx.particles.emit(ev.x, y, ev.z, this.lowQ ? 10 : 20, 12, 0xfff7ea, 0.9, 0.4, 1, 0, 3, 3);
          fx.rings.add(ev.x, gy + 0.25, ev.z, ev.r, 0.9, c, 3, 2.5);       // the shockwave, out to the shove radius
          fx.rings.add(ev.x, gy + 0.3, ev.z, ev.r * 0.7, 0.6, 0xffffff, 2, 1.2);
          fx.rings.add(ev.x, y, ev.z, 8, 0.5, c, 2.5, 1);
          fx.flashes.add(ev.x, y, ev.z, 5.5, 0.32, c, 1.2);
          fx.lights.flash(ev.x, y, ev.z, c, 90, 0.7, 30);
          this.glow.flash(ev.x, ev.z, ev.r, c, 1.0, 0.8);
          // the shell bursts: dark fragments + accent shards, then a ground-hugging dust ring
          fx.shards.emit(ev.x, y, ev.z, this.lowQ ? 24 : 48, 16, 0x2e2a34, 0.75, 1.3, 9, 18, 0.9, 1.2);
          fx.shatter(ev.x, y, ev.z, this.lowQ ? 14 : 28, 18, c, 0.6);
          for (let k = 0; k < (this.lowQ ? 16 : 32); k++) { // a thin dust ring racing out along the ground (outside the body)
            const a = (k / 32) * Math.PI * 2 + R() * 0.2, s = 16 + R() * 8;
            fx.particles.emitDir(ev.x + Math.cos(a) * 4, gy + 0.3, ev.z + Math.sin(a) * 4, Math.cos(a) * s, 0.4 + R() * 0.5, Math.sin(a) * s, 0xb8a890, 0.8, 0.55, 1.1);
          }
          fx.shake = Math.max(fx.shake, 0.85);
          this.push = 1; this.pushX = ev.x; this.pushZ = ev.z;
          this.hitstop = Math.max(this.hitstop, 0.12);
        } else {
          fx.rings.add(ev.x, gy + 0.2, ev.z, 5, 0.5, c, 1.5, 1);
        }
        break;
      }
      case 'bossSlam': {
        const gy = this.ground(ev.x, ev.z);
        fx.rings.add(ev.x, gy + 0.2, ev.z, ev.r, 0.55, ev.color, 2.6, 2);
        fx.rings.add(ev.x, gy + 0.25, ev.z, ev.r * 0.6, 0.4, 0xffffff, 1.5, 1);
        fx.particles.emit(ev.x, gy + 0.4, ev.z, this.lowQ ? 16 : 30, 10, 0xb8a890, 1.0, 0.8, 3, 6, 2, 0.9);
        fx.shatter(ev.x, gy + 0.5, ev.z, 12, 10, ev.color, 0.5);
        this.glow.flash(ev.x, ev.z, ev.r, ev.color, 0.8, 0.45);
        fx.lights.flash(ev.x, gy + 1, ev.z, ev.color, 30, 0.35, ev.r * 2);
        fx.shake = Math.min(1, fx.shake + 0.45);
        break;
      }
      case 'bossMark': break; // telegraph decal: hazardView.ts draws sim.bossMarks
      case 'bossMarkHit': {
        const gy = this.ground(ev.x, ev.z);
        // the falling body: a bright streak from above, then the impact
        for (let k = 0; k < 6; k++) fx.particles.emitDir(ev.x, gy + 8 - k * 1.2, ev.z, 0, -30, 0, ev.color, 0.7, 0.18, 2.4);
        fx.stars.add(ev.x, gy + 0.8, ev.z, ev.r * 1.1, 0.18, ev.color, 2);
        fx.rings.add(ev.x, gy + 0.2, ev.z, ev.r * 1.5, 0.45, ev.color, 2.2, 1.5);
        fx.particles.emit(ev.x, gy + 0.3, ev.z, this.lowQ ? 8 : 16, 7, 0xb8a890, 0.8, 0.6, 3, 8, 2, 0.9);
        fx.shatter(ev.x, gy + 0.6, ev.z, 8, 9, ev.color, 0.45);
        this.glow.flash(ev.x, ev.z, ev.r * 1.2, ev.color, 0.7, 0.35);
        fx.shake = Math.min(1, fx.shake + 0.18);
        break;
      }
    }
  }

  /** per frame: the beat's charge-up, the form's aura and orbiting motes */
  update(sim: Sim, dt: number, t: number): void {
    this.push = Math.max(0, this.push - dt * 1.6);
    const e = sim.boss; if (!e || !e.alive) return;
    const fi = sim.bossFormInfo(e); if (!fi) return;
    const gy = this.ground(e.x, e.z), fx = this.fx, c = fi.accent;
    const look = formLook(fi.boss, fi.form);
    if (fi.transforming > 0) {
      const u = 1 - fi.transforming / TRANSFORM_T, pre = u * TRANSFORM_T < TRANSFORM_BURST;
      if (pre) { // energy pours in: motes from a wide sphere converge on the core, light columns rise around it
        const n = this.lowQ ? 3 : 6, core = gy + 4.2;
        for (let k = 0; k < n; k++) {
          const a = R() * Math.PI * 2, el = (R() - 0.3) * 1.2, r = 7 + R() * 4, life = 0.45;
          const x = e.x + Math.cos(a) * Math.cos(el) * r, y = core + Math.sin(el) * r * 0.6, z = e.z + Math.sin(a) * Math.cos(el) * r;
          fx.particles.emitDir(x, y, z, (e.x - x) / life, (core - y) / life, (e.z - z) / life, k & 1 ? c : 0xffffff, 0.32, life, 2.6);
        }
        if (R() < 0.5) { const a = R() * Math.PI * 2, r = 3 + R() * 3; fx.particles.emitDir(e.x + Math.cos(a) * r, gy + 0.2, e.z + Math.sin(a) * r, 0, 9 + R() * 5, 0, c, 0.5, 0.6, 2.2); }
        if (R() < 0.08 * FLASH.L.flicker + 0.02) fx.stars.add(e.x, core, e.z, 1.5 + u * 3, 0.12, c, 1.6);
      }
      return;
    }
    if (fi.form > 0) {
      if (R() < (this.lowQ ? 0.25 : 0.5)) { const a = R() * Math.PI * 2, r = 1 + R() * 2.5; fx.particles.emitDir(e.x + Math.cos(a) * r, gy + 1 + R() * 5, e.z + Math.sin(a) * r, 0, 2.5, 0, c, 0.4, 0.7, 2.2); }
      const o = look?.orbit;
      if (o) {
        this.orbitA += dt * (1.2 + fi.form * 0.5);
        const per = this.lowQ ? 1 : 2;
        for (let k = 0; k < per; k++) {
          const i = Math.floor(R() * o.n), a = this.orbitA + (i / o.n) * Math.PI * 2, w = 1.2 + fi.form * 0.5, yy = gy + o.y + Math.sin(t * 2 + i) * 0.4;
          fx.particles.emitDir(e.x + Math.cos(a) * o.r, yy, e.z + Math.sin(a) * o.r, -Math.sin(a) * o.r * w, 0, Math.cos(a) * o.r * w, o.color, o.size, 0.35, 2.4);
        }
      }
    }
  }
}
